import * as THREE from "three";
import { TARGET_IMAGE_DIMENSIONS_CANVAS } from "utils/constant.utils";
import { Colors } from "utils/theme.utils";
import { updateTargetImage } from "store/reducers/editorSlice";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { TRANSFORM_BOX_RENDER_ORDER_OFFSET } from "./transform_box.helper";

export const initializeWorkspace = (workspace: WorkspaceContext) => {
    const { rendererRef, canvasRef, sceneRef, cameraRef, targetImageRef } = workspace.refs;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentElement;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const camera = new THREE.OrthographicCamera(-containerWidth / 2, containerWidth / 2, containerHeight / 2, -containerHeight / 2, 0.1, 10);
    camera.position.z = 1;
    cameraRef.current = camera;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const renderer = new THREE.WebGLRenderer({ antialias: true, canvas });
    renderer.sortObjects = false;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(containerWidth, containerHeight);
    renderer.setClearColor(new THREE.Color().setStyle(Colors.canvasBackground), 1);
    rendererRef.current = renderer;

    const animate = () => {
        requestAnimationFrame(animate);
        const targetChildren = targetImageRef.current?.children;
        if (targetChildren) {
            // A transform box lives beside its object and follows its renderOrder (it can change
            // while the box is up, e.g. on reorder), so it stays stacked with that object.
            targetChildren.forEach((child) => {
                const ownerId = child.userData.associatedObjectID;
                if (ownerId === undefined) return;
                const owner = targetChildren.find((sibling) => sibling.userData.id === ownerId);
                if (owner) child.renderOrder = owner.renderOrder + TRANSFORM_BOX_RENDER_ORDER_OFFSET;
            });
            targetChildren.sort((a, b) => a.renderOrder - b.renderOrder);
        }
        renderer.render(scene, camera);
    };
    animate();

    return { camera, scene, renderer };
}

export const renderTargetImage = (workspace: WorkspaceContext, targetImageSrc: string | null): Promise<THREE.Group | null> => {
    const { rendererRef, sceneRef, cameraRef, loaderRef, targetImageRef, currentTargetImageSrcRef, isTargetImageLoadingRef } = workspace.refs;
    const { dispatch, selectedTargetIndex, selectedTargetImageID, targetImageDataList } = workspace;
    return new Promise((resolve) => {

        if (!sceneRef.current || !cameraRef.current || !rendererRef.current) {
            resolve(null);
            return;
        }

        // Prevent multiple simultaneous calls
        if (isTargetImageLoadingRef.current) {
            resolve(null);
            return;
        }

        isTargetImageLoadingRef.current = true;


        const scene = sceneRef.current;

        // Enhanced cleanup: Remove ALL target image groups from scene
        const targetImageGroups = scene.children.filter(child =>
            child.userData.name === "targetImageGroup"
        );

        targetImageGroups.forEach(group => {
            scene.remove(group);

            // Dispose of materials and geometries
            group.traverse((object) => {
                if (object instanceof THREE.Mesh) {
                    if (object.material) {
                        if (Array.isArray(object.material)) {
                            object.material.forEach(mat => {
                                if (mat.map) mat.map.dispose();
                                mat.dispose();
                            });
                        } else {
                            if (object.material.map) object.material.map.dispose();
                            object.material.dispose();
                        }
                    }
                    if (object.geometry) {
                        object.geometry.dispose();
                    }
                }
            });
        });

        // Clean up the existing targetImageRef
        if (targetImageRef.current) {
            // Additional safety check - remove from scene if still there
            if (scene.children.includes(targetImageRef.current)) {
                scene.remove(targetImageRef.current);
            }
            targetImageRef.current = null;
        }

        if (!targetImageSrc) {
            isTargetImageLoadingRef.current = false;
            resolve(null);
            return;
        }

        currentTargetImageSrcRef.current = targetImageSrc;

        const loader = new THREE.TextureLoader();
        loaderRef.current = loader;
        loader.load(targetImageSrc ?? "",
            (texture) => {
                // Double-check that we still don't have duplicates before adding new one
                const existingGroups = scene.children.filter(child =>
                    child.userData.name === "targetImageGroup"
                );

                if (existingGroups.length > 0) {
                    console.warn("Found existing target image groups, removing them");
                    existingGroups.forEach(group => scene.remove(group));
                }

                const { width, height } = targetImageDataList[selectedTargetIndex ?? 0]?.selectedTargetImageDimension;

                // The backend compiles every upload whole (see getDefaultCrop in the image-target
                // compiler), so the tracked area has the upload's own aspect ratio. The canvas box
                // uses the same ratio, so object position/scale line up with what 8th Wall tracks.
                const ratio = width / height;
                let imageWidth = 0;
                let imageHeight = 0;

                if (width > height) {
                    imageWidth = TARGET_IMAGE_DIMENSIONS_CANVAS
                    imageHeight = TARGET_IMAGE_DIMENSIONS_CANVAS / ratio;
                } else {
                    imageHeight = TARGET_IMAGE_DIMENSIONS_CANVAS;
                    imageWidth = TARGET_IMAGE_DIMENSIONS_CANVAS * ratio;
                }

                const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];

                if (currentTargetImage.containedTargetImageDimension.width !== imageWidth || currentTargetImage.containedTargetImageDimension.height !== imageHeight) {
                    dispatch(updateTargetImage({
                        id: selectedTargetImageID ?? "",
                        targetImage: targetImageSrc,
                        targetId: currentTargetImage.targetId,
                        selectedTargetImageDimension: { width: width, height: height },
                        containedTargetImageDimension: { width: imageWidth, height: imageHeight },
                        objects: targetImageDataList[selectedTargetIndex ?? 0]?.objects || [],
                    }))
                }

                const targetImageGroup = new THREE.Group();
                targetImageGroup.renderOrder = 0;
                targetImageGroup.userData.id = targetImageDataList[selectedTargetIndex ?? 0]?.id;
                targetImageGroup.userData.name = "targetImageGroup";
                targetImageGroup.position.set(0, 0, 0);

                texture.needsUpdate = true;

                const geometry = new THREE.PlaneGeometry(1, 1);
                const opacity = 0.3;
                const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: opacity })

                const mesh = new THREE.Mesh(geometry, material);
                mesh.renderOrder = 0;
                mesh.scale.set(imageWidth, imageHeight, 1);

                targetImageGroup.add(mesh);
                targetImageRef.current = targetImageGroup;
                scene.add(targetImageGroup);

                if (rendererRef.current && cameraRef.current) {
                    rendererRef.current.render(scene, cameraRef.current);
                }
                isTargetImageLoadingRef.current = false;
                // updatePlaneSize();
                resolve(targetImageGroup);
            },
            undefined,
            (error) => {
                console.error("Error loading target image texture:", error);
                isTargetImageLoadingRef.current = false;
                resolve(null);
            }
        )
    })
}

export const cleanupObjects = (workspace: WorkspaceContext, includeTargetImage: boolean = false) => {
    const { sceneRef, targetImageRef, objectsRef } = workspace.refs;
    if (!sceneRef.current) return;

    Array.from(objectsRef.current.entries()).forEach(([objectId, objectData]) => {
        if (objectData.cleanup) {
            objectData.cleanup();
        }
        objectsRef.current.delete(objectId);
    });

    const scene = sceneRef.current;

    // Helper function for disposing materials safely
    const disposeMaterial = (material) => {
        if (!material) return;

        if (Array.isArray(material)) {
            // Handle array of materials
            material.forEach(mat => {
                // Check for map texture on materials that might have it
                if (mat.map) {
                    mat.map.dispose();
                }
                mat.dispose();
            });
        } else {
            // Handle single material
            // Check for map texture on materials that might have it
            if (material.map) {
                material.map.dispose();
            }
            material.dispose();
        }
    };

    // Create a copy of children array to avoid issues with removing during iteration
    const childrenToCheck = [...scene.children];

    // Remove all objects EXCEPT the targetImageRef.current
    childrenToCheck.forEach(child => {
        // Skip the target image - this is the key comparison that ensures we don't remove it
        if (child === targetImageRef.current) {
            return;
        }

        // Skip if it's a camera or other essential component
        if (!(child instanceof THREE.Mesh) &&
            !(child instanceof THREE.Sprite) &&
            !(child instanceof THREE.Group)) {
            return;
        }

        // Remove from scene
        scene.remove(child);

        // Dispose of materials and geometry
        if (child instanceof THREE.Mesh || child instanceof THREE.Sprite) {
            if (child.material) {
                disposeMaterial(child.material);
            }

            if (child.geometry) {
                child.geometry.dispose();
            }
        } else if (child instanceof THREE.Group) {
            // Handle groups by recursively removing their children
            child.traverse((object) => {
                if (object instanceof THREE.Mesh || object instanceof THREE.Sprite) {
                    if (object.material) {
                        disposeMaterial(object.material);
                    }

                    if (object.geometry) {
                        object.geometry.dispose();
                    }
                }
            });
        }
    });

    if (includeTargetImage) {
        if (targetImageRef.current) {
            targetImageRef.current.traverse((object) => {
                if (object instanceof THREE.Mesh || object instanceof THREE.Sprite) {
                    if (object.material) {
                        disposeMaterial(object.material);
                    }
                    if (object.geometry) {
                        object.geometry.dispose();
                    }
                }
            });

            sceneRef.current.remove(targetImageRef.current);
            targetImageRef.current = null;
        }
    }
};
